import { toast } from 'react-toastify';
import { clearAllCookies } from './cookieFunctions';

interface ApiResponseError {
  code?: string;
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
}

export const handleApiError = (error: ApiResponseError): void => {
  if (error.code === 'ERR_NETWORK') {
    toast.error('Network error!', {
      position: 'top-right',
    });
    return;
  }

  if (error.response && error.response.status) {
    switch (error.response.status) {
      case 400:
        toast.error(error.response.data?.message ?? 'Invalid Parameters.', {
          position: 'top-right',
        });
        return;

      case 401:
        clearAllCookies();
        window.location.href = '/';
        return;

      case 403:
        toast.error(
          error.response.data?.message ?? 'Permission denied. Please try again.',
          { position: 'top-right' }
        );
        clearAllCookies();
        window.location.href = '/auth/signin';
        return;

      case 404:
        toast.error(error.response.data?.message ?? 'Not found.', {
          position: 'top-right',
        });
        return;

      case 409:
        toast.error(error.response.data?.message ?? 'Not found.', {
          position: 'top-right',
        });
        return;

      default:
        toast.error('Something went wrong!', {
          position: 'top-right',
        });
        return;
    }
  }
};

export const stages: Record<number, string> = {
  0: 'Exit Pass Issued',
  1: 'Gate Entry',
  2: 'Bill Entry',
  3: 'Lab Check',
  4: 'Concern',
  5: 'PD Approval',
  6: 'Unload  Approval',
  7: 'Unload Completion',
  8: 'Exit Gate Pass',
  9: 'Cancelled',
};

export const loadingStage: Record<number, string> = {
  0: 'Exit Pass Issued',
  1: 'Do an order',
  2: 'Approve Loading',
  3: 'Loading Completion',
  4: 'Lab Check',
  5: 'Customer detail',
  6: 'Exit Pass',
  7: 'Cancelled',
};

/**
 * Same formulae as Load Completion: Auto DY uses Math.ceil((kg / ltr) * 1000).
 * Difference uses ACT DY as an integer: Math.ceil(kg / (actDy / 1000)) - ltr.
 */
export const calcLoadingDensity = (
  kgInput: unknown,
  ltrInput: unknown,
  actDyInput?: unknown,
) => {
  const kg = parseFloat(String(kgInput ?? ''));
  const ltr = parseFloat(String(ltrInput ?? ''));
  const actDy = parseInt(String(actDyInput ?? ''), 10);

  const autoDy =
    !isNaN(kg) && !isNaN(ltr) && ltr !== 0
      ? Math.ceil((kg / ltr) * 1000)
      : 0;

  const differenceDueToDensity =
    !isNaN(kg) && !isNaN(ltr) && !isNaN(actDy) && actDy !== 0
      ? Math.ceil(kg / (actDy / 1000)) - ltr
      : 0;

  return { autoDy, differenceDueToDensity };
};

export const getFromLocalStorage = <T>(key: string, initialValue: T): T => {
  try {
    const item = window.localStorage.getItem(key);
    return item ? JSON.parse(item) : initialValue;
  } catch (error) {
    console.error('Error getting data from local storage:', error);
    return initialValue;
  }
};

export const setToLocalStorage = <T>(key: string, value: T): void => {
  try {
    const serializedValue = JSON.stringify(value);
    window.localStorage.setItem(key, serializedValue);
  } catch (error) {
    console.error('Error setting data to local storage:', error);
  }
};
